import { datadogRum } from '@datadog/browser-rum'
import { isArray, isBoolean, isPlainObject } from 'lodash'

import {
  getOAuthSubject as getOAuthSubjectUtil,
  getUserRegardAdmin as getUserRegardAdminUtil,
} from '~/notes/utils/cookies'
import { getFlagFromLocalStorageOrWindow } from '~/notes/utils/localStorageFlags'

import { UserFlag, UserFlagObject } from './types'
import {
  flagHasVariant,
  getFlagVariantValue,
  isGroupFlagEnabled,
  UnleashClient,
  unleashClient as regardUnleashClient,
} from './unleash'

interface IsFlagEnabledForUserOptions<K extends keyof Flags> {
  defaultValue?: boolean
  enableForAllInternalUsers?: boolean
  getFlagValue?: (key: K) => Flags[K] | undefined
  getIsRegardAdmin?: () => boolean
  getOAuthSubject?: () => string
}

const defaultOptions = {
  getFlagValue: getFlagFromLocalStorageOrWindow,
  getOAuthSubject: getOAuthSubjectUtil,
  getIsRegardAdmin: getUserRegardAdminUtil,
  enableForAllInternalUsers: false,
  defaultValue: false,
} as const

const isFlagEnabledInUnleash = <K extends keyof Flags>(
  flagKey: K,
  unleashClient: UnleashClient,
  defaultValue: boolean = false,
): boolean => {
  let unleashEnabled = unleashClient.isEnabled(flagKey)
  if (unleashEnabled) {
    const variant = unleashClient.getVariant(flagKey)
    const hasVariant = flagHasVariant(variant)
    if (hasVariant) {
      const variantValue = getFlagVariantValue(flagKey, variant)
      unleashEnabled = !!variantValue
    }
  }
  return unleashEnabled || defaultValue
}

// This helper function is encapsulating the mess of feature flag styles. We should
// unroll this and simplify over time as we condense ways of defining feature flags.
const evaluateWhetherFlagIsEnabledForUser = <K extends keyof Flags>({
  defaultValue,
  enableForAllInternalUsers,
  flag,
  flagKey,
  isRegardAdmin,
  oauthSubject,
  unleashClient,
}: {
  defaultValue: boolean
  enableForAllInternalUsers: boolean
  flag: UserFlag
  flagKey: K
  isRegardAdmin: boolean
  oauthSubject: string
  unleashClient: UnleashClient | undefined
}): boolean => {
  if (enableForAllInternalUsers && isRegardAdmin) {
    return true
  }

  if (isBoolean(flag)) {
    return flag
  }

  if (isArray(flag)) {
    return flag.includes(oauthSubject)
  }

  if (flag && isPlainObject(flag)) {
    const flagObject = flag as UserFlagObject
    if (flagObject.exclude?.includes(oauthSubject)) return false
    if (flagObject.include?.includes(oauthSubject)) return true
    return flagObject.default ?? defaultValue
  }

  if (flag !== undefined || unleashClient === undefined) {
    return defaultValue
  }

  return isFlagEnabledInUnleash(flagKey, unleashClient, defaultValue)
}

export const isFlagEnabledForUser = <K extends keyof Flags>(
  flagKey: K,
  options: IsFlagEnabledForUserOptions<K> = {},
  unleashClient: UnleashClient = regardUnleashClient,
): boolean => {
  const {
    getFlagValue,
    getOAuthSubject,
    getIsRegardAdmin,
    enableForAllInternalUsers,
    defaultValue,
  } = { ...defaultOptions, ...options }

  const flag = getFlagValue(flagKey) as UserFlag
  if (flag === 'FORCE_DISABLE') {
    return false
  }
  const oauthSub = getOAuthSubject()
  // The main client context already includes the user's primary groupId, so NOT_IN constraints
  // evaluate correctly server-side. isGroupFlagEnabled covers any additional group IDs.
  const flagEnabled =
    evaluateWhetherFlagIsEnabledForUser({
      flag,
      flagKey,
      oauthSubject: oauthSub,
      enableForAllInternalUsers,
      isRegardAdmin: getIsRegardAdmin(),
      defaultValue,
      unleashClient,
    }) || isGroupFlagEnabled(flagKey)

  datadogRum.addFeatureFlagEvaluation(flagKey, flagEnabled)
  return flagEnabled
}

export const createIsFlagEnabledForUserGetter =
  <K extends keyof Flags>(
    flagKey: K,
    options: IsFlagEnabledForUserOptions<K> = {},
    unleashClient: UnleashClient = regardUnleashClient,
  ): (() => boolean) =>
  () =>
    isFlagEnabledForUser(flagKey, options, unleashClient)
